import { useEffect, useMemo, useRef, type ReactNode } from 'react';
import { RUBRIC_LIBRARY } from '../../core/library';
import { EXAMPLES, type ExampleId, type WritingExample } from '../../core/sample';
import { splitSentences, type Sentence } from '../../core/sentences';

/**
 * The writing. In edit mode a large, paste-friendly textarea with a live sentence count;
 * in results mode the same text rebuilt from sentence offsets, each sentence its own span
 * so an item's evidence can light up. Both modes keep the text out of session replay.
 */

export interface EditProps {
  mode: 'edit';
  text: string;
  onChange: (text: string) => void;
  examples?: readonly WritingExample[];
  /** The example last loaded, or '' when the writing is the teacher's own. */
  exampleId?: ExampleId | '';
  /** Load an example and its rubric. */
  onPickExample?: (id: ExampleId) => void;
  disabled?: boolean;
  /** Status shown at the sheet's foot: why Score is disabled, or that scoring is running. */
  footNote?: ReactNode;
}

export interface ResultsProps {
  mode: 'results';
  text: string;
  sentences: Sentence[];
  /** Sentence id → probability, for the selected item. Empty when nothing is selected. */
  highlights: ReadonlyMap<string, number>;
}

export type TextPaneProps = EditProps | ResultsProps;

export default function TextPane(props: TextPaneProps) {
  if (props.mode === 'edit') return <EditPane {...props} />;
  return <ResultsPane {...props} />;
}

function EditPane({ text, onChange, disabled, examples = EXAMPLES, exampleId = '', onPickExample, footNote }: EditProps) {
  const count = useMemo(() => splitSentences(text).length, [text]);
  const selected = examples.find((example) => example.id === exampleId);
  return (
    <section className="writing-sheet" aria-labelledby="writing-heading">
      <h2 id="writing-heading" className="visually-hidden">Writing</h2>
      <textarea
        className="writing ph-mask"
        aria-label="Writing"
        placeholder="Paste the student's writing here."
        value={text}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        spellCheck={false}
      />
      <div className="sheet-foot">
      <div className="example-picker">
        <label className="field">
          <span>Try an example</span>
          <select value={exampleId} disabled={disabled} onChange={(event) => { if (event.target.value) onPickExample?.(event.target.value); }}>
            <option value="">Choose an example…</option>
            {RUBRIC_LIBRARY.map((entry) => {
              const group = examples.filter((example) => example.rubricId === entry.id);
              return group.length > 0 && (
                <optgroup key={entry.id} label={entry.rubric.title}>
                  {group.map((example) => <option key={example.id} value={example.id}>{example.name}</option>)}
                </optgroup>
              );
            })}
          </select>
        </label>
        {selected && <p className="hint">{`${selected.note} ${provenance(selected)}`}</p>}
      </div>
      <p className="pane-foot" aria-live="polite">
        {count === 1 ? '1 sentence' : `${count} sentences`}
        {footNote}
      </p>
      </div>
    </section>
  );
}

const provenance = (example?: WritingExample): string =>
  example?.source
    ? `${example.source.author}, ${example.source.year}; public domain.`
    : 'Written for this tool; not a real response.';

function ResultsPane({ text, sentences, highlights }: ResultsProps) {
  const read = useRef<HTMLDivElement>(null);
  // Bring the first evidence sentence into view inside the pane, without moving the page.
  useEffect(() => {
    const container = read.current;
    const first = container?.querySelector<HTMLElement>('.sentence.evidence');
    if (container && first) container.scrollTo?.({ top: Math.max(0, first.offsetTop - 24), behavior: 'smooth' });
  }, [highlights]);
  const nodes: ReactNode[] = [];
  let cursor = 0;
  for (const s of sentences) {
    if (s.start > cursor) nodes.push(text.slice(cursor, s.start));
    const strength = highlights.get(s.id);
    nodes.push(
      <span
        key={s.id}
        data-sentence-id={s.id}
        className={strength !== undefined ? 'sentence evidence' : 'sentence'}
        style={strength !== undefined ? ({ '--strength': strength } as React.CSSProperties) : undefined}
      >
        {s.text}
      </span>,
    );
    cursor = s.end;
  }
  if (cursor < text.length) nodes.push(text.slice(cursor));

  return (
    <section className="pane pane-text" aria-labelledby="writing-heading">
      {/* Nothing above the text: the way back and the sentence count are in the results bar. */}
      <h2 id="writing-heading" className="visually-hidden">Writing</h2>
      <div ref={read} className="writing writing-read ph-no-capture" data-testid="writing-read">{nodes}</div>
    </section>
  );
}
